import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { renderKitPreview, getKitStyleById } from '../utils/kitUtils';

interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  teamName: string;
  isActive: boolean;
  isEmailVerified: boolean;
  role: 'user' | 'admin';
  lastLogin?: string;
  favouriteTeam?: {
    id: string;
    name: string;
    shortName?: string;
    logoUrl?: string;
  };
  kitStyle?: string;
  kitPrimaryColour?: string;
  kitSecondaryColour?: string;
  preferences: {
    defaultLanguage: string;
    timezone: string;
    emailNotifications: boolean;
  };
  createdAt: string;
}

interface Team {
  id: string;
  name: string;
  shortName?: string;
  logoUrl?: string;
  countryCode?: string;
}

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    teamName: ''
  });
  const [teams, setTeams] = useState<Team[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedFavouriteTeam, setSelectedFavouriteTeam] = useState<string | null>(null);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        teamName: user.teamName || ''
      });
      setSelectedFavouriteTeam(user.favouriteTeam?.id || 'none');
      // Initialize email notifications toggle from user's actual preferences
      setEmailNotifications(user.preferences?.emailNotifications ?? true);
    }
  }, [user]);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoadingTeams(true);
        const response = await apiClient.get('/teams');
        setTeams(response.data);
      } catch (err: any) {
        console.error('Failed to load teams:', err);
      } finally {
        setLoadingTeams(false);
      }
    };

    fetchTeams();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate form data
      if (!formData.firstName.trim()) {
        throw new Error('First name is required');
      }
      if (!formData.lastName.trim()) {
        throw new Error('Last name is required');
      }
      if (!formData.email.trim()) {
        throw new Error('Email is required');
      }
      if (!formData.teamName.trim()) {
        throw new Error('Team name is required');
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      const response = await apiClient.put('/users/profile', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        teamName: formData.teamName.trim(),
        favouriteTeamId: selectedFavouriteTeam === 'none' ? null : selectedFavouriteTeam
      });

      // Update the user context with new data
      if (updateUser) {
        updateUser(response.data.user);
      }

      setSuccess('Profile updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-xl">Please log in to view your profile.</div>
      </div>
    );
  }

  return (
    <div className="" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-2xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Home
          </Link>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Profile Settings</h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>Update your personal information</p>
        </div>

        <div className="card rounded-lg shadow p-6 mb-6">

         {/* Kit Designer Link */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Team Kit
                </h3>
                <Link
                  to="/kit-designer"
                  className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                >

              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" >
              <g clip-path="url(#clip0_4_214)">
              <path d="M15 4L21 6V11H18V19C18 19.2652 17.8946 19.5196 17.7071 19.7071C17.5196 19.8946 17.2652 20 17 20H7C6.73478 20 6.48043 19.8946 6.29289 19.7071C6.10536 19.5196 6 19.2652 6 19V11H3V6L9 4C9 4.79565 9.31607 5.55871 9.87868 6.12132C10.4413 6.68393 11.2044 7 12 7C12.7956 7 13.5587 6.68393 14.1213 6.12132C14.6839 5.55871 15 4.79565 15 4Z" stroke="white" />
              </g>
              <defs>
              <clipPath id="clip0_4_214">
              <rect width="24" height="24" fill="white"/>
              </clipPath>
              </defs>
              </svg> &nbsp;

                  {user?.kitStyle ? 'Edit Team Kit' : 'Design Your Team Kit'}
                </Link>
              </div>
              
              {user?.kitStyle ? (
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-20 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    {(() => {
                      const kitStyle = getKitStyleById(user.kitStyle);
                      console.log('Kit preview debug:', {
                        kitStyle: user.kitStyle,
                        kitStyleFound: !!kitStyle,
                        primaryColor: user.kitPrimaryColour,
                        secondaryColor: user.kitSecondaryColour
                      });
                      if (kitStyle) {
                        return renderKitPreview(
                          kitStyle, 
                          user.kitPrimaryColour || '#2563eb', 
                          user.kitSecondaryColour || '#ffffff', 
                          'small'
                        );
                      }
                      return <div className="text-xs text-gray-500">Kit not found</div>;
                    })()}
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {user.kitStyle.charAt(0).toUpperCase() + user.kitStyle.slice(1)} Style
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Primary: <span className="inline-block w-3 h-3 rounded-full mr-1" style={{ backgroundColor: user.kitPrimaryColour }}></span>
                      Secondary: <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: user.kitSecondaryColour }}></span>
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  No kit designed yet. Click the button above to create your team's unique kit design!
                </p>
              )}
           

          </div>

        {/* Profile Form */}
        <div className="card rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error/Success Messages */}
            {error && (
              <div className="border px-4 py-3 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444', color: '#ef4444' }}>
                {error}
              </div>
            )}
            {success && (
              <div className="border px-4 py-3 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderColor: '#22c55e', color: '#22c55e' }}>
                {success}
              </div>
            )}


            

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="input-field mt-1 block w-full rounded-md px-3 py-2"
                placeholder="Enter your first name"
              />
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="input-field mt-1 block w-full rounded-md px-3 py-2"
                placeholder="Enter your last name"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="input-field mt-1 block w-full rounded-md px-3 py-2"
                placeholder="Enter your email address"
              />
            </div>

            {/* Team Name */}
            <div>
              <label htmlFor="teamName" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Team Name *
              </label>
              <input
                type="text"
                id="teamName"
                name="teamName"
                value={formData.teamName}
                onChange={handleInputChange}
                required
                className="input-field mt-1 block w-full rounded-md px-3 py-2"
                placeholder="Enter your team name"
              />
              <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                This is the name that will appear in leagues and leaderboards
              </p>
            </div>

            {/* Favourite Team */}
            <div>
              <label htmlFor="favouriteTeam" className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Favourite Team
              </label>
              <select
                id="favouriteTeam"
                value={selectedFavouriteTeam || ''}
                onChange={(e) => setSelectedFavouriteTeam(e.target.value === '' ? null : e.target.value)}
                className="input-field mt-1 block w-full rounded-md px-3 py-2"
                disabled={loadingTeams}
              >
                <option value="">Select your favourite team</option>
                <option value="none">None, I'm a neutral</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                Choose your favourite team to personalize your experience
              </p>
            </div>

            {/* Email Notifications Toggle */}
            <div className="border-t pt-6 mt-6" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Email Notifications
                  </h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Receive updates about your predictions, league standings, and important announcements
                  </p>
                </div>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={async () => {
                      if (emailNotifications) {
                        setShowUnsubscribeConfirm(true);
                      } else {
                        // User wants to re-subscribe
                        try {
                          setLoading(true);
                          const response = await apiClient.put('/users/profile', {
                            preferences: {
                              emailNotifications: true
                            },
                            unsubscribed: false
                          });
                          
                          // Update the user context with new data
                          if (updateUser) {
                            updateUser(response.data.user);
                          }
                          
                          setEmailNotifications(true);
                          setSuccess('You have been re-subscribed to email notifications');
                        } catch (err: any) {
                          setError(err.response?.data?.message || 'Failed to update email preferences');
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      emailNotifications ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        emailNotifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-between pt-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary font-bold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
              
              <Link
                to="/"
                className="font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Cancel
              </Link>
            </div>


          </form>
        </div>

        {/* Additional Info */}
        <div className="mt-6 rounded-lg p-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
          <h3 className="text-sm font-medium mb-2" style={{ color: '#3b82f6' }}>Profile Information</h3>
          <p className="text-sm" style={{ color: '#1d4ed8' }}>
            Your profile information is used across the platform. Your team name will be visible to other users in leagues and leaderboards.
          </p>
        </div>
      </div>

      {/* Unsubscribe Confirmation Dialog */}
      {showUnsubscribeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                    Unsubscribe from emails?
                  </h3>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Are you sure you want to unsubscribe from all email notifications? You'll miss important updates about:
                </p>
                <ul className="mt-3 text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
                  <li>• League standings and results</li>
                  <li>• Prediction reminders</li>
                  <li>• Trophy awards and achievements</li>
                  <li>• Important platform announcements</li>
                </ul>
                <p className="mt-3 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  You can always re-enable notifications later in your profile settings.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowUnsubscribeConfirm(false)}
                  className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                  style={{ 
                    color: 'var(--text-secondary)', 
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const response = await apiClient.put('/users/profile', {
                        preferences: {
                          emailNotifications: false
                        },
                        unsubscribed: true
                      });
                      
                      // Update the user context with new data
                      if (updateUser) {
                        updateUser(response.data.user);
                      }
                      
                      setEmailNotifications(false);
                      setShowUnsubscribeConfirm(false);
                      setSuccess('You have been unsubscribed from email notifications');
                    } catch (err: any) {
                      setError(err.response?.data?.message || 'Failed to update email preferences');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Yes, Unsubscribe'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 